import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import BorrowerDashboard from './pages/borrower/Dashboard';
import BorrowerDeals from './pages/borrower/Deals';
import BorrowerDealCreate from './pages/borrower/DealCreate';
import BorrowerDealDetail from './pages/borrower/DealDetail';
import BorrowerPaymentSuccess from './pages/borrower/PaymentSuccess';
import BorrowerVerification from './pages/borrower/Verification';
import BorrowerPaymentAuthorized from './pages/borrower/PaymentAuthorized';

import LSPDashboard from './pages/lsp/Dashboard';
import LSPVerification from './pages/lsp/Verification';
import LSPMarketplace from './pages/lsp/Marketplace';
import LSPCommitments from './pages/lsp/Commitments';

import AdminDashboard from './pages/admin/Dashboard';
import AdminDeals from './pages/admin/Deals';
import AdminLSPVerifications from './pages/admin/LSPVerifications';
import AdminCommitments from './pages/admin/Commitments';
import AdminAuditLogs from './pages/admin/AuditLogs';
import AdminDocumentReview from './pages/admin/DocumentReview';

import Home from './pages/Home';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import './App.css';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Borrower Routes */}
      <Route path="/borrower" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerDashboard /></PrivateRoute>} />
      <Route path="/borrower/deals" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerDeals /></PrivateRoute>} />
      <Route path="/borrower/deals/create" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerDealCreate /></PrivateRoute>} />
      <Route path="/borrower/deals/:dealId" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerDealDetail /></PrivateRoute>} />
      <Route path="/borrower/payment-success" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerPaymentSuccess /></PrivateRoute>} />
      <Route path="/borrower/verification" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerVerification /></PrivateRoute>} />
      <Route path="/borrower/payment-authorized" element={<PrivateRoute allowedRoles={['borrower']}><BorrowerPaymentAuthorized /></PrivateRoute>} />

      {/* LSP Routes */}
      <Route path="/lsp" element={<PrivateRoute allowedRoles={['lsp']}><LSPDashboard /></PrivateRoute>} />
      <Route path="/lsp/verification" element={<PrivateRoute allowedRoles={['lsp']}><LSPVerification /></PrivateRoute>} />
      <Route path="/lsp/marketplace" element={<PrivateRoute allowedRoles={['lsp']}><LSPMarketplace /></PrivateRoute>} />
      <Route path="/lsp/commitments" element={<PrivateRoute allowedRoles={['lsp']}><LSPCommitments /></PrivateRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<PrivateRoute allowedRoles={['admin']}><AdminDashboard /></PrivateRoute>} />
      <Route path="/admin/deals" element={<PrivateRoute allowedRoles={['admin']}><AdminDeals /></PrivateRoute>} />
      <Route path="/admin/lsp-verifications" element={<PrivateRoute allowedRoles={['admin']}><AdminLSPVerifications /></PrivateRoute>} />
      <Route path="/admin/commitments" element={<PrivateRoute allowedRoles={['admin']}><AdminCommitments /></PrivateRoute>} />
      <Route path="/admin/audit-logs" element={<PrivateRoute allowedRoles={['admin']}><AdminAuditLogs /></PrivateRoute>} />
      <Route path="/admin/document-review" element={<PrivateRoute allowedRoles={['admin']}><AdminDocumentReview /></PrivateRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;